import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";
import { categories, allArticles } from "@/mocks/movies";

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeSlug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const categoryArticles = allArticles.filter((a) => a.category === activeSlug);

  const pageTitle = `${currentCategory.name} - 8090电影网怀旧影视`;
  const pageDesc = `${currentCategory.description}，8090电影网为您精选${currentCategory.name}经典作品，重温童年回忆。`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: pageTitle,
    description: pageDesc,
    url: `https://8090movie.com/category/${activeSlug}`,
    isPartOf: {
      "@type": "WebSite",
      name: "8090电影网",
      url: "https://8090movie.com",
    },
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`${currentCategory.name},8090电影网,怀旧影视,老电影在线,童年回忆剧`}
        canonicalUrl={`https://8090movie.com/category/${activeSlug}`}
        jsonLd={jsonLd}
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-50">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              { label: currentCategory.name },
            ]}
          />
        </div>

        {/* Category Header */}
        <section className="w-full bg-background-50 py-8 md:py-12">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center gap-4 mb-3">
              <div className="w-14 h-14 flex items-center justify-center bg-primary-50 rounded-2xl">
                <i className={`${currentCategory.icon} text-2xl text-primary-600`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
                  {currentCategory.name}
                </h1>
                <p className="text-foreground-600 text-sm mt-1">{currentCategory.description}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="w-full bg-white border-b border-background-200 sticky top-16 z-40">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto overflow-x-auto">
            <div className="flex items-center gap-1 py-2 min-w-max">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => {
                    setActiveSlug(cat.slug);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className={`px-4 py-2 text-sm rounded-full whitespace-nowrap cursor-pointer transition-colors ${
                    activeSlug === cat.slug
                      ? "bg-primary-500 text-white font-medium"
                      : "text-foreground-600 hover:text-foreground-900 hover:bg-background-100"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Article Grid */}
        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryArticles.map((article) => (
                  <article
                    key={article.id}
                    className="group bg-white rounded-xl border border-background-200 overflow-hidden hover:border-primary-300 transition-all"
                  >
                    <Link
                      to={`/article/${article.id}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                      className="block cursor-pointer"
                    >
                      <div className="relative h-52 overflow-hidden">
                        <img
                          src={article.imageUrl}
                          alt={`${article.title} 经典剧照`}
                          title={`${article.title} - 8090电影网怀旧影视 ${currentCategory.name}`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="px-2.5 py-1 bg-primary-500 text-white text-xs font-medium rounded-full">
                            {article.year}
                          </span>
                        </div>
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="flex items-center gap-1 text-xs text-accent-600">
                            <i className="ri-star-fill text-xs"></i>
                            {article.rating}
                          </span>
                          {article.director && (
                            <span className="text-xs text-foreground-500">
                              导演: {article.director}
                            </span>
                          )}
                        </div>
                        <h3 className="font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors text-sm">
                          {article.title}
                        </h3>
                        <p className="text-xs text-foreground-600 line-clamp-2 leading-relaxed">
                          {article.excerpt}
                        </p>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
                  <i className="ri-film-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
                <Link
                  to="/"
                  className="inline-block mt-4 text-sm text-primary-600 hover:text-primary-700 cursor-pointer"
                >
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}